import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  Users, 
  Plus, 
  Pencil, 
  Trash2,
  Check,
  X,
  KeyRound
} from "lucide-react";

const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Administrador" },
  { value: "gestor_local", label: "Gestor Local" },
  { value: "gestor_empresa", label: "Gestor de Empresa" },
  { value: "operador_parque", label: "Operador de Parque" }
];

const ROLE_LABELS = {
  super_admin: "Super Admin",
  gestor_local: "Gestor Local",
  gestor_empresa: "Gestor Empresa",
  operador_parque: "Operador"
};

export default function UsersPage() {
  const { user: currentUser, isSuperAdmin, isGestorLocal, isGestorEmpresa } = useAuth();
  const [users, setUsers] = useState([]);
  const [locais, setLocais] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    password: "",
    role: "operador_parque",
    empresa_id: "",
    locais_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, locaisRes, empresasRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/locais`),
        axios.get(`${API}/empresas`)
      ]);
      setUsers(usersRes.data);
      setLocais(locaisRes.data);
      setEmpresas(empresasRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const getAllowedRoles = () => {
    if (isSuperAdmin) return ROLE_OPTIONS;
    if (isGestorLocal) return ROLE_OPTIONS.filter(r => ["gestor_local", "gestor_empresa", "operador_parque"].includes(r.value));
    if (isGestorEmpresa) return ROLE_OPTIONS.filter(r => r.value === "gestor_empresa");
    return [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        nome: formData.nome,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        empresa_id: formData.empresa_id || null
      };

      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, {
          nome: formData.nome,
          role: formData.role,
          empresa_id: formData.empresa_id || null,
          status_aprovacao: formData.status_aprovacao,
          locais_ids: formData.locais_ids
        });
        toast.success("Utilizador atualizado");
      } else {
        await axios.post(`${API}/users`, payload);
        toast.success("Utilizador criado");
      }
      setDialogOpen(false);
      setEditingUser(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar utilizador");
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      nome: user.nome,
      email: user.email,
      password: "",
      role: user.role,
      empresa_id: user.empresa_id || "",
      status_aprovacao: user.status_aprovacao,
      locais_ids: user.locais_ids || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!confirm("Tem a certeza que deseja eliminar este utilizador?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Utilizador eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar utilizador");
    }
  };

  const toggleApproval = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, {
        status_aprovacao: !user.status_aprovacao
      });
      toast.success(user.status_aprovacao ? "Utilizador desativado" : "Utilizador aprovado");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar estado");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPasswordUser || !newPassword) return;
    try {
      await axios.put(`${API}/users/${resetPasswordUser.id}`, {
        new_password: newPassword
      });
      toast.success(`Password de ${resetPasswordUser.nome} reposta com sucesso`);
      setResetPasswordUser(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao repor password");
    }
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      email: "",
      password: "",
      role: "operador_parque",
      empresa_id: "",
      locais_ids: []
    });
  };

  const getEmpresaName = (empresaId) => empresas.find(e => e.id === empresaId)?.nome_empresa || "-";

  const headerAction = (
    <Dialog open={dialogOpen} onOpenChange={(open) => {
      setDialogOpen(open);
      if (!open) {
        setEditingUser(null);
        resetForm();
      }
    }}>
      <DialogTrigger asChild>
        <Button className="bg-[#0047FF] hover:bg-[#0035CC]" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Utilizador
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingUser ? "Editar Utilizador" : "Novo Utilizador"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome</Label>
            <Input
              id="nome"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              placeholder="Nome completo"
              required
              data-testid="user-nome-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@exemplo.com"
              required
              disabled={!!editingUser}
              data-testid="user-email-input"
            />
          </div>
          {!editingUser && (
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                data-testid="user-password-input"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label>Perfil</Label>
            <Select 
              value={formData.role} 
              onValueChange={(value) => setFormData({ ...formData, role: value })}
            >
              <SelectTrigger data-testid="user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getAllowedRoles().map((role) => (
                  <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(formData.role === "gestor_empresa") && (
            <div className="space-y-2">
              <Label>Empresa</Label>
              <Select 
                value={formData.empresa_id} 
                onValueChange={(value) => setFormData({ ...formData, empresa_id: value })}
              >
                <SelectTrigger data-testid="user-empresa-select">
                  <SelectValue placeholder="Selecione uma empresa" />
                </SelectTrigger>
                <SelectContent>
                  {empresas.map((empresa) => (
                    <SelectItem key={empresa.id} value={empresa.id}>{empresa.nome_empresa}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {editingUser && (formData.role === "gestor_local" || formData.role === "operador_parque") && (
            <div className="space-y-2">
              <Label>Locais Atribuídos</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
                {locais.map((local) => (
                  <label key={local.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.locais_ids.includes(local.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, locais_ids: [...formData.locais_ids, local.id] });
                        } else {
                          setFormData({ ...formData, locais_ids: formData.locais_ids.filter(id => id !== local.id) });
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">{local.nome}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-[#0047FF] hover:bg-[#0035CC]" data-testid="user-submit-btn">
              {editingUser ? "Guardar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <PageLayout title="Utilizadores" subtitle="Gestão de acessos e permissões" headerAction={headerAction}>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF]"></div>
        </div>
      ) : users.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#64748B] mb-4">Nenhum utilizador registado</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Perfil</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">{user.nome}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{ROLE_LABELS[user.role] || user.role}</Badge>
                    </TableCell>
                    <TableCell>{getEmpresaName(user.empresa_id)}</TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant={user.status_aprovacao ? "default" : "destructive"}
                        className={user.status_aprovacao ? "bg-[#00C853]" : "bg-[#D50000]"}
                      >
                        {user.status_aprovacao ? "Ativo" : "Pendente"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {isSuperAdmin && user.id !== currentUser?.id && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => toggleApproval(user)}
                            title={user.status_aprovacao ? "Desativar" : "Aprovar"}
                            data-testid={`toggle-approval-${user.id}`}
                          >
                            {user.status_aprovacao ? <X className="w-4 h-4 text-[#D50000]" /> : <Check className="w-4 h-4 text-[#00C853]" />}
                          </Button>
                        )}
                        {user.id !== currentUser?.id && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => { setResetPasswordUser(user); setNewPassword(""); }}
                            title="Repor Password"
                            data-testid={`reset-password-${user.id}`}
                          >
                            <KeyRound className="w-4 h-4 text-[#FF9800]" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(user)} data-testid={`edit-user-${user.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {user.id !== currentUser?.id && (
                          <Button variant="ghost" size="icon" className="text-[#D50000]" onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={(open) => { if (!open) { setResetPasswordUser(null); setNewPassword(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Repor Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-sm text-[#64748B]">
              Definir nova password para <span className="font-semibold text-[#0F172A]">{resetPasswordUser?.nome}</span> ({resetPasswordUser?.email})
            </p>
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nova password"
                required
                minLength={6}
                data-testid="reset-password-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setResetPasswordUser(null)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-[#FF9800] hover:bg-[#F57C00] text-white" data-testid="reset-password-submit-btn">
                Repor Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
